// The Copilot drawer: the Hiring agents working beside the person, on every
// console page. Server rules: server/src/copilot/copilot.service.js.
//
// What the person sees:
//   - a right-side drawer (full width on a phone) with a short line saying
//     what it is looking at — the vacancy, the stage, the candidate;
//   - 3–4 suggested questions for that page;
//   - the conversation, with "working…" lines while a tool runs;
//   - CARDS for anything that would change something or reach a candidate,
//     each with exactly what will happen and Confirm / Cancel. Only the
//     person's click confirms; the assistant cannot.
//
// Keyboard: the top-bar button (or Ctrl+/) opens it and puts the cursor in
// the question box; Esc closes it and returns focus to the button; Enter
// sends, Shift+Enter is a new line. New messages are announced politely to
// screen readers.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Sparkles, X, Send, Loader2, CheckCircle2, XCircle, AlertTriangle, PauseCircle, RotateCcw, ExternalLink,
} from 'lucide-react';
import { useCopilot } from './CopilotContext';
import { copilotStatus, streamChat, confirmCard, cancelCard } from '../../services/copilot.service';

const FIXED_TABS = new Set(['Job Description', 'Media Plan & Content', 'Assessment Pipeline', 'Resumes', 'Offer Letter', 'Questionnaire', 'Rounds']);

/* What is on screen, from the address and from what the page told us. */
function usePageContext() {
  const location = useLocation();
  const { focus } = useCopilot();
  return useMemo(() => {
    const path = location.pathname;
    const page = { path };
    const panel = matchPath('/jobs/:jobId/human-interview/:stageId/panel/:resumeId', path);
    const reports = matchPath('/jobs/:jobId/reports/*', path) || matchPath('/jobs/:jobId/reports', path);
    const job = matchPath('/jobs/:id', path);
    if (panel) {
      page.jobId = panel.params.jobId;
      page.stageId = panel.params.stageId;
      page.candidateId = panel.params.resumeId;
    } else if (reports) {
      page.jobId = reports.params.jobId;
    } else if (job && !['create', 'generate'].includes(job.params.id)) {
      page.jobId = job.params.id;
      const tab = new URLSearchParams(location.search).get('tab');
      if (tab && !FIXED_TABS.has(tab)) page.stageLabel = tab;
    }
    if (focus?.stageId) page.stageId = focus.stageId;
    if (focus?.candidateId) page.candidateId = focus.candidateId;
    let kind = 'other';
    if (page.candidateId) kind = 'candidate';
    else if (page.jobId) kind = 'job';
    else if (/^\/dashboard/.test(path)) kind = 'dashboard';
    else if (/^\/jobs\/?$/.test(path)) kind = 'jobs';
    else if (/^\/assessment-approvals/.test(path)) kind = 'assessments';
    else if (/^\/talent-pool/.test(path)) kind = 'talent';
    return { page, kind, candidateName: focus?.candidateName || '', stageLabel: focus?.stageLabel || page.stageLabel || '' };
  }, [location.pathname, location.search, focus]);
}

function suggestionsFor(kind, { paused, canChange }) {
  const change = (s) => (canChange ? [s] : []);
  switch (kind) {
    case 'candidate':
      return [
        'Summarise this candidate',
        'Where is this candidate in the process?',
        ...change('Move this candidate to the next stage'),
        paused ? 'Has this candidate paid the application fee?' : 'Draft an interview invitation for this candidate',
      ].slice(0, 4);
    case 'job':
      return [
        ...change('Draft the assessment pipeline for this role'),
        ...change('Write the written test for this role'),
        'How many applications are paid?',
        'Who are the top CV scores for this role?',
      ].slice(0, 4);
    case 'jobs':
      return [
        'How many applications does each role have?',
        ...change('Draft a new vacancy for TGT English, Grades 6–10'),
        'Which vacancies are paused or closed?',
        'Which roles have no applicants yet?',
      ].slice(0, 4);
    case 'assessments':
      return [
        'Which assessment proposals are waiting for approval?',
        'Which stages are not ready for candidates yet?',
        'Who approves question papers here?',
      ];
    case 'talent':
      return ['Who are the strongest CVs across all roles?', 'How many applicants have not paid?', "Give me today's hiring summary"];
    default:
      return [
        "Give me today's hiring summary",
        'Who has not paid the application fee?',
        'Are there questions from candidates waiting for us?',
        'Which roles have no applicants yet?',
      ];
  }
}

/* Links in answers: inside the console they move the page, not the tab. */
function MdLink({ href, children }) {
  if (href && href.startsWith('/')) return <Link to={href} className="text-dps-orange-dark underline underline-offset-2">{children}</Link>;
  return <a href={href} target="_blank" rel="noreferrer noopener" className="text-dps-orange-dark underline underline-offset-2">{children}</a>;
}

function Answer({ text }) {
  return (
    <div className="prose prose-sm max-w-none text-dps-ink prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5 prose-strong:text-dps-ink">
      <ReactMarkdown components={{ a: MdLink }}>{text}</ReactMarkdown>
    </div>
  );
}

/* ── Cards ─────────────────────────────────────────────────────────────── */

function Row({ label, children }) {
  if (children === undefined || children === null || children === '') return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="label-dps !text-[0.625rem] !tracking-wider w-24 shrink-0 pt-0.5">{label}</span>
      <span className="text-dps-ink min-w-0 break-words">{children}</span>
    </div>
  );
}

function CardBody({ card }) {
  switch (card.type) {
    case 'message':
      return (
        <div className="space-y-2">
          <Row label="To">{[card.recipient?.name, card.recipient?.role].filter(Boolean).join(' · ')}</Row>
          <Row label="Email">{card.recipient?.email}</Row>
          <Row label="WhatsApp">{card.recipient?.phone}</Row>
          {(card.messages || []).map((m, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="rounded-lg border border-dps-hairline bg-dps-bg p-3">
              <div className="label-dps !text-[0.625rem] mb-1">{m.channel}</div>
              {m.subject && <div className="text-sm font-semibold text-dps-ink mb-1">{m.subject}</div>}
              <div className="text-sm text-dps-ink whitespace-pre-wrap">{m.text}</div>
            </div>
          ))}
        </div>
      );
    case 'move':
      return (
        <div className="space-y-1">
          <Row label="Candidate">{card.candidate}</Row>
          <Row label="Role">{card.role}</Row>
          <Row label="Now">{card.from}</Row>
          <Row label="Moves to">{card.to}</Row>
        </div>
      );
    case 'vacancy': {
      const l = card.listingAfter || {};
      return (
        <div className="space-y-1">
          <Row label="Title">{l.title}</Row>
          <Row label="Subject">{l.subject || l.area}</Row>
          <Row label="Grades">{l.gradeBand}</Row>
          <Row label="Closes">{l.closesOn || l.closingDate}</Row>
          {l.summary && <p className="text-sm text-dps-ink-2 line-clamp-4 pt-1">{l.summary}</p>}
        </div>
      );
    }
    case 'pipeline': {
      const removed = (card.removed || []).map((s) => (typeof s === 'string' ? { label: s } : s));
      return (
        <div className="space-y-2">
          {card.before?.length > 0 && <Row label="Now">{card.before.join(' → ')}</Row>}
          <ol className="list-decimal pl-5 text-sm text-dps-ink space-y-0.5">
            {(card.stages || []).map((s) => (
              <li key={s.stageId || s.label}>
                {s.label}
                {s.change === 'new' && <span className="ml-1.5 text-xs font-semibold text-dps-green-dark">new</span>}
                {s.change === 'moved' && <span className="ml-1.5 text-xs text-dps-ink-2">moved</span>}
                {s.change === 'renamed' && <span className="ml-1.5 text-xs text-dps-ink-2">(was {s.renamedFrom})</span>}
              </li>
            ))}
          </ol>
          {removed.length > 0 && (
            <div className="rounded-lg border border-dps-status-red bg-dps-status-red-soft p-3 space-y-1.5" role="alert">
              <div className="text-sm font-semibold text-dps-status-red">Removed</div>
              <ul className="text-sm text-dps-status-red space-y-0.5">
                {removed.map((s) => (
                  <li key={s.stageId || s.label} className="line-through">
                    {s.label}
                    {s.candidatesInStage > 0 && <span className="no-underline"> ({s.candidatesInStage} candidate{s.candidatesInStage === 1 ? '' : 's'} in it)</span>}
                  </li>
                ))}
              </ul>
              {card.removalWarning && <p className="text-sm text-dps-status-red">{card.removalWarning}</p>}
            </div>
          )}
          {card.switchOffWarning && (
            <p className="text-sm text-dps-status-red" role="alert">{card.switchOffWarning}</p>
          )}
          {(card.paperWarnings || []).map((w) => (
            <p key={w.proposalId || w.stageId} className="text-sm text-dps-ink-2">{w.warning}</p>
          ))}
          {card.proposedBy && <Row label="Proposed by">{card.proposedBy}</Row>}
        </div>
      );
    }
    case 'assessment':
      return (
        <div className="space-y-2">
          <Row label="Stage">{card.stage}</Row>
          <Row label="Written by">{card.draftedBy}</Row>
          {card.needsTeacherCheck > 0 && (
            <div className="pill-dps !bg-dps-status-yellow-soft !text-dps-ink">{card.needsTeacherCheck} need a teacher&apos;s check</div>
          )}
          <details className="text-sm">
            <summary className="cursor-pointer text-dps-orange-dark font-semibold">See the {card.questions?.length || 0} questions</summary>
            <ol className="list-decimal pl-5 mt-2 space-y-1 text-dps-ink max-h-64 overflow-auto">
              {(card.questions || []).map((q) => (
                <li key={q.n}>{q.text}{q.needsTeacherCheck ? ' (needs a teacher’s check)' : ''}</li>
              ))}
            </ol>
          </details>
        </div>
      );
    case 'new_vacancy':
      return (
        <div className="space-y-1">
          <Row label="Title">{card.vacancy?.title}</Row>
          <Row label="Subject">{card.vacancy?.subject}</Row>
          <Row label="Grades">{card.vacancy?.gradeBand}</Row>
          <Row label="Closes">{card.vacancy?.closingDate}</Row>
          <Row label="Description">{card.descriptionBy}</Row>
        </div>
      );
    default:
      return null;
  }
}

function ActionCard({ card, onDecided }) {
  const [state, setState] = useState(card.status || 'pending');
  const [message, setMessage] = useState('');
  const [link, setLink] = useState(null);
  // A pipeline card that removes stages needs a second, separate "yes".
  const [removalOk, setRemovalOk] = useState(false);
  const needsAck = Boolean(card.requiresRemovalAck);
  const expired = state === 'pending' && card.validUntil && new Date(card.validUntil) < new Date();

  const decide = async (fn, busy, body) => {
    setState(busy);
    const out = await fn(card.actionId, body);
    setMessage(out.message || '');
    if (out.link) setLink(out.link);
    if (out.ok) setState(out.status);
    else setState(out.status === 'refused' ? 'pending' : 'failed');
    onDecided?.(card, out);
  };

  const done = ['done', 'cancelled', 'failed'].includes(state);
  return (
    <section
      className="rounded-xl border border-dps-edge bg-white shadow-dps-sm overflow-hidden"
      aria-label={card.title}
    >
      <header className="card-dps-header !px-4 !py-2.5 !text-sm flex items-center gap-2">
        <AlertTriangle size={16} aria-hidden="true" />
        <span>{card.title}</span>
      </header>
      <div className="p-4 space-y-3">
        <CardBody card={card} />
        {card.whatHappens && <p className="text-xs text-dps-ink-2">{card.whatHappens}</p>}
        {card.link && (
          <Link to={card.link.to} className="inline-flex items-center gap-1 text-sm text-dps-orange-dark underline underline-offset-2">
            {card.link.label} <ExternalLink size={14} aria-hidden="true" />
          </Link>
        )}
        {message && (
          <p role="status" className={`text-sm flex items-start gap-1.5 ${state === 'done' ? 'text-dps-green-dark' : state === 'cancelled' ? 'text-dps-ink-2' : 'text-dps-status-red'}`}>
            {state === 'done' ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" aria-hidden="true" /> : <XCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />}
            <span>{message}</span>
          </p>
        )}
        {link && (
          <Link to={link.to} className="inline-flex items-center gap-1 text-sm text-dps-orange-dark underline underline-offset-2">{link.label}</Link>
        )}
        {expired && !message && <p className="text-sm text-dps-ink-2">This card has expired. Ask again to see the current version.</p>}
        {!done && !expired && needsAck && (
          <label className="flex items-start gap-2 text-sm text-dps-status-red font-semibold">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={removalOk}
              disabled={state !== 'pending'}
              onChange={(e) => setRemovalOk(e.target.checked)}
            />
            <span>{card.removalAckLabel || 'Yes, remove these stages'}</span>
          </label>
        )}
        {!done && !expired && (
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              className="btn-dps-accent !px-4 !py-2 text-sm"
              disabled={state !== 'pending' || (needsAck && !removalOk)}
              onClick={() => decide(confirmCard, 'confirming', needsAck ? {
                acknowledgeRemovals: true,
                // One tick covers stages removed and stages switched off (HIR-HR2).
                removedStageIds: [...(card.removed || []), ...(card.switchedOff || [])].map((s) => s.stageId).filter(Boolean),
              } : {})}
            >
              {state === 'confirming' ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <CheckCircle2 size={16} aria-hidden="true" />}
              {card.confirmLabel || 'Confirm'}
            </button>
            <button
              type="button"
              className="btn-dps-outline !px-4 !py-2 text-sm"
              disabled={state !== 'pending'}
              onClick={() => decide(cancelCard, 'cancelling')}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── The drawer ────────────────────────────────────────────────────────── */

export function CopilotButton() {
  const { open, setOpen } = useCopilot();
  return (
    <button
      type="button"
      id="copilot-toggle"
      onClick={() => setOpen(!open)}
      aria-expanded={open}
      aria-controls="copilot-panel"
      title="Copilot (Ctrl+/)"
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-dps font-heading font-semibold text-sm transition
        ${open ? 'bg-dps-orange text-white' : 'bg-dps-orange-soft text-dps-orange-dark hover:bg-dps-orange hover:text-white'}`}
    >
      <Sparkles size={16} aria-hidden="true" />
      <span>Copilot</span>
    </button>
  );
}

export default function CopilotPanel() {
  const {
    open, setOpen, entries, setEntries, transcript, setTranscript, reset,
  } = useCopilot();
  const ctx = usePageContext();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [working, setWorking] = useState('');
  const inputRef = useRef(null);
  const endRef = useRef(null);
  const abortRef = useRef(null);

  // Ctrl+/ toggles from anywhere; Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') { e.preventDefault(); setOpen((o) => !o); }
      else if (e.key === 'Escape' && open) {
        setOpen(false);
        document.getElementById('copilot-toggle')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    copilotStatus().then(setStatus).catch(() => setStatus({ canUse: false, error: true }));
  }, [open]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [entries, working]);

  const ask = useCallback(async (raw) => {
    const text = String(raw || '').trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    setWorking('Thinking');
    setEntries((e) => [...e, { kind: 'user', text }]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamChat({
        message: text,
        history: transcript,
        page: ctx.page,
        signal: controller.signal,
        onEvent: (ev) => {
          if (ev.type === 'working') setWorking(ev.text);
          else if (ev.type === 'text' && ev.text) setEntries((e) => [...e, { kind: 'assistant', text: ev.text }]);
          else if (ev.type === 'card') setEntries((e) => [...e, { kind: 'card', card: ev.card }]);
          else if (ev.type === 'notice') setEntries((e) => [...e, { kind: 'notice', text: ev.text }]);
          else if (ev.type === 'reply') setEntries((e) => [...e, { kind: 'assistant', text: ev.text }]);
          else if (ev.type === 'error') setEntries((e) => [...e, { kind: 'error', text: ev.message }]);
          else if (ev.type === 'done') setTranscript((t) => [...t, ...(ev.transcript || [])].slice(-40));
        },
      });
    } catch (err) {
      if (err.name !== 'AbortError') setEntries((e) => [...e, { kind: 'error', text: err.message }]);
    } finally {
      setBusy(false);
      setWorking('');
      abortRef.current = null;
    }
  }, [busy, ctx.page, setEntries, setTranscript, transcript]);

  /* The model learns what the person decided, as part of the conversation. */
  const onDecided = useCallback((card, out) => {
    const said = out.ok
      ? `(I pressed ${out.status === 'cancelled' ? 'Cancel' : 'Confirm'} on "${card.title}". Result: ${out.message})`
      : `(I pressed Confirm on "${card.title}" but it was not done: ${out.message})`;
    setTranscript((t) => [...t, { role: 'user', text: said }, { role: 'assistant', text: 'Noted.', toolCalls: [] }].slice(-40));
  }, [setTranscript]);

  if (!open) return null;

  const paused = Boolean(status?.messagesPaused);
  const canChange = Boolean(status?.canChange);
  const suggestions = suggestionsFor(ctx.kind, { paused, canChange });
  const looking = [
    ctx.page.jobId ? 'this vacancy' : null,
    ctx.stageLabel ? `the ${ctx.stageLabel} stage` : null,
    ctx.candidateName || (ctx.page.candidateId ? 'this candidate' : null),
  ].filter(Boolean).join(' · ');

  return (
    <aside
      id="copilot-panel"
      aria-label="Copilot"
      className="fixed z-50 inset-y-0 right-0 w-full sm:w-[420px] max-w-full flex flex-col bg-dps-bg border-l border-dps-hairline shadow-dps-lg font-body"
      style={{ height: '100dvh', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <header className="flex items-center gap-2 px-4 py-3 bg-white border-b border-dps-hairline">
        <span className="w-8 h-8 rounded-full bg-dps-orange-soft text-dps-orange-dark flex items-center justify-center" aria-hidden="true">
          <Sparkles size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading font-bold text-dps-ink text-base leading-tight">Copilot</h2>
          <p className="text-xs text-dps-ink-3 truncate">{looking ? `Looking at ${looking}` : 'Hiring, at your school, as you'}</p>
        </div>
        <button type="button" className="btn-dps-ghost !p-2" onClick={() => { abortRef.current?.abort(); reset(); }} title="Start a new conversation" aria-label="Start a new conversation">
          <RotateCcw size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="btn-dps-ghost !p-2"
          onClick={() => { setOpen(false); document.getElementById('copilot-toggle')?.focus(); }}
          aria-label="Close Copilot"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </header>

      {paused && (
        <div className="flex items-start gap-2 px-4 py-2 bg-dps-status-yellow-soft text-dps-ink text-xs border-b border-dps-hairline" role="note">
          <PauseCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>Candidate messages are paused for this school. The Copilot will not draft or send any.</span>
        </div>
      )}
      {status && status.canUse && !canChange && (
        <div className="px-4 py-2 bg-white text-dps-ink-2 text-xs border-b border-dps-hairline" role="note">
          You can ask and look. Changes need the recruitment write permission.
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" aria-live="polite" aria-relevant="additions">
        {status && status.canUse === false && (
          <p className="text-sm text-dps-ink-2">
            {status.error ? 'The Copilot cannot be reached just now.' : 'The Copilot needs a recruitment permission at this school. Ask your school administrator.'}
          </p>
        )}
        {entries.length === 0 && (
          <div className="text-sm text-dps-ink-2 space-y-2">
            <p>Ask about vacancies, candidates and assessments at your school. I act as you, with your permissions, and everything is recorded under your name.</p>
            <p>Anything that would change something or reach a candidate comes to you as a card first — nothing happens until you press Confirm.</p>
          </div>
        )}
        {entries.map((e, i) => {
          const key = `${e.kind}-${i}`;
          if (e.kind === 'user') {
            return (
              <div key={key} className="flex justify-end">
                <div className="max-w-[85%] rounded-xl rounded-br-sm bg-dps-orange-soft text-dps-ink px-3 py-2 text-sm whitespace-pre-wrap">{e.text}</div>
              </div>
            );
          }
          if (e.kind === 'card') return <ActionCard key={e.card.actionId || key} card={e.card} onDecided={onDecided} />;
          if (e.kind === 'notice') {
            return (
              <div key={key} className="flex items-start gap-2 rounded-lg bg-dps-status-yellow-soft px-3 py-2 text-sm text-dps-ink">
                <PauseCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" /><span>{e.text}</span>
              </div>
            );
          }
          if (e.kind === 'error') {
            return <div key={key} role="alert" className="rounded-lg bg-dps-status-red-soft px-3 py-2 text-sm text-dps-ink">{e.text}</div>;
          }
          return (
            <div key={key} className="max-w-[95%] rounded-xl rounded-bl-sm bg-white border border-dps-hairline px-3 py-2">
              <Answer text={e.text} />
            </div>
          );
        })}
        {working && (
          <div className="flex items-center gap-2 text-sm text-dps-ink-3" role="status">
            <Loader2 size={16} className="animate-spin text-dps-orange" aria-hidden="true" />
            <span>{working}…</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {!busy && status?.canUse !== false && (
        <div className="px-4 pt-2 flex flex-wrap gap-2" aria-label="Suggested questions">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => ask(s)}
              className="text-left text-xs px-3 py-1.5 rounded-pill border border-dps-edge bg-white text-dps-ink-2 hover:border-dps-orange hover:text-dps-orange-dark focus:outline-none focus:ring-[3px] focus:ring-dps-orange-soft"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        className="p-3 flex items-end gap-2"
        onSubmit={(e) => { e.preventDefault(); ask(input); }}
      >
        <label htmlFor="copilot-input" className="sr-only">Ask the Copilot</label>
        <textarea
          id="copilot-input"
          ref={inputRef}
          rows={2}
          value={input}
          maxLength={4000}
          disabled={status?.canUse === false}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(input); }
          }}
          placeholder={busy ? 'Working…' : 'Ask about this page, a candidate, a vacancy…'}
          className="input-dps resize-none text-sm !py-2"
        />
        <button type="submit" className="btn-dps !px-3 !py-2.5" disabled={busy || !input.trim()} aria-label="Send">
          {busy ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Send size={18} aria-hidden="true" />}
        </button>
      </form>
      <p className="px-4 pb-2 text-[0.6875rem] text-dps-ink-4">
        Offers are issued from the Offer Letter tab. Question papers are approved on the{' '}
        <button type="button" className="underline" onClick={() => navigate('/assessment-approvals')}>Assessments page</button>.
      </p>
    </aside>
  );
}
